import { useState, useMemo, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';

const COUNTRY_CODES = [
  { code: '+44',  flag: '🇬🇧', name: 'United Kingdom' },
  { code: '+1',   flag: '🇺🇸', name: 'USA / Canada' },
  { code: '+20',  flag: '🇪🇬', name: 'Egypt' },
  { code: '+27',  flag: '🇿🇦', name: 'South Africa' },
  { code: '+31',  flag: '🇳🇱', name: 'Netherlands' },
  { code: '+32',  flag: '🇧🇪', name: 'Belgium' },
  { code: '+33',  flag: '🇫🇷', name: 'France' },
  { code: '+34',  flag: '🇪🇸', name: 'Spain' },
  { code: '+36',  flag: '🇭🇺', name: 'Hungary' },
  { code: '+39',  flag: '🇮🇹', name: 'Italy' },
  { code: '+40',  flag: '🇷🇴', name: 'Romania' },
  { code: '+41',  flag: '🇨🇭', name: 'Switzerland' },
  { code: '+46',  flag: '🇸🇪', name: 'Sweden' },
  { code: '+47',  flag: '🇳🇴', name: 'Norway' },
  { code: '+48',  flag: '🇵🇱', name: 'Poland' },
  { code: '+49',  flag: '🇩🇪', name: 'Germany' },
  { code: '+55',  flag: '🇧🇷', name: 'Brazil' },
  { code: '+60',  flag: '🇲🇾', name: 'Malaysia' },
  { code: '+61',  flag: '🇦🇺', name: 'Australia' },
  { code: '+62',  flag: '🇮🇩', name: 'Indonesia' },
  { code: '+63',  flag: '🇵🇭', name: 'Philippines' },
  { code: '+64',  flag: '🇳🇿', name: 'New Zealand' },
  { code: '+66',  flag: '🇹🇭', name: 'Thailand' },
  { code: '+81',  flag: '🇯🇵', name: 'Japan' },
  { code: '+82',  flag: '🇰🇷', name: 'South Korea' },
  { code: '+86',  flag: '🇨🇳', name: 'China' },
  { code: '+90',  flag: '🇹🇷', name: 'Turkey' },
  { code: '+91',  flag: '🇮🇳', name: 'India' },
  { code: '+92',  flag: '🇵🇰', name: 'Pakistan' },
  { code: '+93',  flag: '🇦🇫', name: 'Afghanistan' },
  { code: '+94',  flag: '🇱🇰', name: 'Sri Lanka' },
  { code: '+98',  flag: '🇮🇷', name: 'Iran' },
  { code: '+212', flag: '🇲🇦', name: 'Morocco' },
  { code: '+213', flag: '🇩🇿', name: 'Algeria' },
  { code: '+216', flag: '🇹🇳', name: 'Tunisia' },
  { code: '+218', flag: '🇱🇾', name: 'Libya' },
  { code: '+220', flag: '🇬🇲', name: 'Gambia' },
  { code: '+221', flag: '🇸🇳', name: 'Senegal' },
  { code: '+224', flag: '🇬🇳', name: 'Guinea' },
  { code: '+225', flag: '🇨🇮', name: 'Ivory Coast' },
  { code: '+233', flag: '🇬🇭', name: 'Ghana' },
  { code: '+234', flag: '🇳🇬', name: 'Nigeria' },
  { code: '+249', flag: '🇸🇩', name: 'Sudan' },
  { code: '+250', flag: '🇷🇼', name: 'Rwanda' },
  { code: '+251', flag: '🇪🇹', name: 'Ethiopia' },
  { code: '+254', flag: '🇰🇪', name: 'Kenya' },
  { code: '+255', flag: '🇹🇿', name: 'Tanzania' },
  { code: '+256', flag: '🇺🇬', name: 'Uganda' },
  { code: '+260', flag: '🇿🇲', name: 'Zambia' },
  { code: '+263', flag: '🇿🇼', name: 'Zimbabwe' },
  { code: '+351', flag: '🇵🇹', name: 'Portugal' },
  { code: '+353', flag: '🇮🇪', name: 'Ireland' },
  { code: '+354', flag: '🇮🇸', name: 'Iceland' },
  { code: '+356', flag: '🇲🇹', name: 'Malta' },
  { code: '+358', flag: '🇫🇮', name: 'Finland' },
  { code: '+380', flag: '🇺🇦', name: 'Ukraine' },
  { code: '+386', flag: '🇸🇮', name: 'Slovenia' },
  { code: '+420', flag: '🇨🇿', name: 'Czech Republic' },
  { code: '+421', flag: '🇸🇰', name: 'Slovakia' },
  { code: '+880', flag: '🇧🇩', name: 'Bangladesh' },
  { code: '+960', flag: '🇲🇻', name: 'Maldives' },
  { code: '+966', flag: '🇸🇦', name: 'Saudi Arabia' },
  { code: '+971', flag: '🇦🇪', name: 'UAE' },
  { code: '+972', flag: '🇮🇱', name: 'Israel' },
  { code: '+973', flag: '🇧🇭', name: 'Bahrain' },
  { code: '+974', flag: '🇶🇦', name: 'Qatar' },
  { code: '+975', flag: '🇧🇹', name: 'Bhutan' },
  { code: '+977', flag: '🇳🇵', name: 'Nepal' },
  { code: '+994', flag: '🇦🇿', name: 'Azerbaijan' },
  { code: '+995', flag: '🇬🇪', name: 'Georgia' },
  { code: '+998', flag: '🇺🇿', name: 'Uzbekistan' },
];

function parse(value: string): { dialCode: string; local: string } {
  if (value.startsWith('+')) {
    const match = COUNTRY_CODES
      .filter(c => value.startsWith(c.code))
      .sort((a, b) => b.code.length - a.code.length)[0];
    if (match) {
      const local = value.slice(match.code.length).replace(/^\s+/, '');
      return { dialCode: match.code, local };
    }
  }
  return { dialCode: '+44', local: value.replace(/\D/g, '') };
}

interface PhoneInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  required?: boolean;
  wrapperClassName?: string;
  leftSlot?: React.ReactNode;
}

export function PhoneInput({
  value,
  onChange,
  placeholder = '07123456789',
  className = '',
  required,
}: PhoneInputProps) {
  const [touched, setTouched] = useState(false);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [openUpward, setOpenUpward] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const { dialCode, local } = useMemo(() => parse(value), [value]);
  const selected = COUNTRY_CODES.find(c => c.code === dialCode) ?? COUNTRY_CODES[0];

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    if (!q) return COUNTRY_CODES;
    return COUNTRY_CODES.filter(c =>
      c.name.toLowerCase().includes(q) || c.code.includes(q)
    );
  }, [search]);

  // UK (+44) requires exactly 10 digits; other countries accept 7–15
  const expectedLength = dialCode === '+44' ? 10 : null;
  const maxDigits = expectedLength ?? 15;
  const isInvalid = touched && local.length > 0 && (
    expectedLength !== null ? local.length !== expectedLength : local.length < 7
  );
  const invalidMsg = dialCode === '+44'
    ? 'UK numbers must be exactly 10 digits after +44 (e.g. 7123456789)'
    : 'Please enter a valid phone number';

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (open) setTimeout(() => searchRef.current?.focus(), 50);
  }, [open]);

  const handleToggle = () => {
    if (!open && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      setOpenUpward(spaceBelow < 280);
    }
    setOpen(o => !o);
  };

  const selectCountry = (code: string) => {
    onChange(`${code} ${local}`);
    setOpen(false);
    setSearch('');
  };

  const handleLocalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const cleaned = e.target.value.replace(/\D/g, '').slice(0, maxDigits);
    onChange(`${dialCode} ${cleaned}`);
  };

  return (
    <div>
      <div className="flex gap-1.5">
        {/* Custom flag dropdown */}
        <div ref={dropdownRef} className="relative flex-shrink-0">
          <button
            ref={triggerRef}
            type="button"
            onClick={handleToggle}
            className="input-premium flex items-center gap-1.5 px-2.5 h-full min-h-[42px] w-[90px] cursor-pointer"
          >
            <span className="text-xl leading-none">{selected.flag}</span>
            <span className="text-sm text-muted-foreground font-medium">{selected.code}</span>
            <ChevronDown size={13} className="text-muted-foreground ml-auto flex-shrink-0" />
          </button>

          {open && (
            <div className={`absolute z-[9999] left-0 w-64 bg-card border border-border rounded-xl shadow-2xl overflow-hidden ${openUpward ? 'bottom-full mb-1' : 'top-full mt-1'}`}>
              {/* Search */}
              <div className="p-2 border-b border-border">
                <input
                  ref={searchRef}
                  type="text"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search country…"
                  className="w-full px-3 py-1.5 text-sm rounded-lg bg-muted border-0 outline-none text-foreground placeholder:text-muted-foreground"
                />
              </div>
              {/* List */}
              <div className="max-h-52 overflow-y-auto">
                {filtered.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No results</p>
                ) : (
                  filtered.map(c => (
                    <button
                      key={c.code}
                      type="button"
                      onClick={() => selectCountry(c.code)}
                      className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-muted transition-colors text-left ${c.code === dialCode ? 'bg-primary/10 text-primary font-semibold' : 'text-foreground'}`}
                    >
                      <span className="text-lg leading-none">{c.flag}</span>
                      <span className="flex-1 truncate">{c.name}</span>
                      <span className="text-muted-foreground text-xs flex-shrink-0">{c.code}</span>
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        {/* Local number input */}
        <input
          type="tel"
          value={local}
          onChange={handleLocalChange}
          onBlur={() => setTouched(true)}
          placeholder={placeholder}
          maxLength={maxDigits}
          required={required}
          className={`input-premium text-sm flex-1 min-w-0${isInvalid ? ' border-destructive ring-2 ring-destructive/20' : ''} ${className}`}
        />
      </div>
      {isInvalid && (
        <p className="text-xs text-destructive mt-1.5">{invalidMsg}</p>
      )}
    </div>
  );
}
